import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
  WritableSignal
} from "@angular/core";

import {FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {AnimationOptions, LottieComponent} from "ngx-lottie";
import type {AnimationItem} from "lottie-web";
import {DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {ButtonModule} from "primeng/button";
import {InputTextModule} from "primeng/inputtext";
import {Subscription} from "rxjs";
import {
  ActionCardComponent,
  CardComponent,
  ContentSlideComponent,
  ContentSliderComponent,
  ContentSliderService,
  contentSliderServiceInjectionToken,
  ContentSliderStateService,
  contentSliderStateServiceInjectionToken,
  ESeverity,
  IContentSliderService,
  IContentSliderStateService,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import {ZTemplateDirective} from "@zaeper/angular-core-lib";
import {IDictionaryService} from "@zaeper/localization-lib";
import {dictionaryServiceInjectionToken} from "@zaeper/angular-localization-lib";
import {
  connectionCodeServiceInjectionToken
} from "../../components/connection-code-display/connection-code-service-injection-token";
import {IConnectionCodeService} from "../../components/connection-code-display/i-connection-code.service";
import {IConnectReceiverModalData} from "./i-connect-receiver-modal-data";
import {IConnectionCodeStateService} from "../../components/connection-code-display/i-connection-code-state.service";
import {
  connectionCodeStateServiceInjectionToken
} from "../../components/connection-code-display/connection-code-state-service-injection-token";
import {
  receiverAuthenticationWsClientInjectionToken
} from "../../ws-clients/receiver-authentication/receiver-authentication-ws-client-injection-token";
import {
  IReceiverAuthenticationWsClient
} from "../../ws-clients/receiver-authentication/i-receiver-authentication-ws.client";
import {
  IReceiverRegisteredMessage
} from "../../ws-clients/receiver-authentication/messages/i-receiver-registered-message";
import {Router} from "@angular/router";
import {sessionStateServiceInjectionToken} from "../../global-states/session/session-state-service-injection-token";
import {ISessionStateService} from "../../global-states/session/i-session-state.service";
import {receiverRestClientInjectionToken} from "../../rest-clients/receiver/receiver-rest-client-injection-token";
import {IReceiverRestClient} from "../../rest-clients/receiver/i-receiver-rest.client";
import {IReceiver} from "../../models/i-receiver";
import {ICloud} from "../../models/i-cloud";
import {cloudRestClientInjectionToken} from "../../rest-clients/cloud/cloud-rest-client-injection-token";
import {groupRestClientInjectionToken} from "../../rest-clients/group/group-rest-client-injection-token";
import {ICloudRestClient} from "../../rest-clients/cloud/i-cloud-rest.client";
import {IGroupRestClient} from "../../rest-clients/group/i-group-rest.client";
import {IGroup} from "../../models/i-group";
import {
  ConnectionCodeDisplayComponent
} from "../../components/connection-code-display/connection-code-display.component";
import {ConnectionCodeService} from "../../components/connection-code-display/connection-code.service";
import {ConnectionCodeStateService} from "../../components/connection-code-display/connection-code-state.service";
import {ILauncher, ILauncherRestClient, launcherRestClientInjectionToken} from "@zaeper/angular-application-store-lib";
import {IPage} from "@zaeper/communication-lib";
import {SelectModule} from "primeng/select";

@Component({
  templateUrl: "connect-receiver-modal.component.html",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CardComponent,
    SelectModule,
    LottieComponent,
    ContentSliderComponent,
    ContentSlideComponent,
    ActionCardComponent,
    ConnectionCodeDisplayComponent,
    ZTemplateDirective
],
  providers: [
    {
      provide: contentSliderStateServiceInjectionToken,
      useClass: ContentSliderStateService
    }, {
      provide: contentSliderServiceInjectionToken,
      useClass: ContentSliderService
    }, {
      provide: connectionCodeServiceInjectionToken,
      useClass: ConnectionCodeService
    }, {
      provide: connectionCodeStateServiceInjectionToken,
      useClass: ConnectionCodeStateService
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConnectReceiverModalComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly lottieConnectToCloud: AnimationOptions = {
    path: "/assets/lottie/connect_to_cloud.json"
  };
  public readonly lottieCheck: AnimationOptions = {
    path: "/assets/lottie/check.json",
    loop: false,
    autoplay: false
  };
  public readonly connectReceiverForm: UntypedFormGroup;
  public readonly cloudId: string | undefined;
  public readonly groupId: string | undefined;
  public readonly launchers$: WritableSignal<ILauncher[]> = signal([]);
  public readonly launchersLoaded$: WritableSignal<boolean> = signal(false);
  public readonly installingLauncher$: WritableSignal<boolean> = signal(false);
  public selectedLauncherId: string | null = null;

  private readonly _lottieAnimations: Map<string, AnimationItem>;
  private readonly _subscriptions: Subscription;

  @ViewChildren(ContentSlideComponent, {read: ElementRef}) private readonly _contentSlides!: QueryList<ElementRef>;

  constructor(
    @Inject(contentSliderServiceInjectionToken) private readonly _contentSliderService: IContentSliderService,
    @Inject(dictionaryServiceInjectionToken) private readonly _dictionaryService: IDictionaryService,
    @Inject(contentSliderStateServiceInjectionToken) private readonly _contentSliderStateService: IContentSliderStateService,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(connectionCodeServiceInjectionToken) private readonly _connectionCodeService: IConnectionCodeService,
    @Inject(connectionCodeStateServiceInjectionToken) private readonly _connectionCodeStateService: IConnectionCodeStateService,
    @Inject(receiverAuthenticationWsClientInjectionToken) private readonly _receiverAuthenticationWsClient: IReceiverAuthenticationWsClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(receiverRestClientInjectionToken) private readonly _receiverRestClient: IReceiverRestClient,
    @Inject(cloudRestClientInjectionToken) private readonly _cloudRestClient: ICloudRestClient,
    @Inject(groupRestClientInjectionToken) private readonly _groupRestClient: IGroupRestClient,
    @Inject(launcherRestClientInjectionToken) private readonly _launcherRestClient: ILauncherRestClient,
    readonly dynamicDialogConfig: DynamicDialogConfig<IConnectReceiverModalData>,
    private readonly _dialogRef: DynamicDialogRef, private readonly _router: Router
  ) {
    this._subscriptions = new Subscription();
    this._lottieAnimations = new Map<string, AnimationItem>();

    this.cloudId = dynamicDialogConfig.data?.cloudId;
    this.groupId = dynamicDialogConfig.data?.groupId;

    this.connectReceiverForm = new UntypedFormGroup({
      connectionCode: new UntypedFormControl("", {
        validators: [
          Validators.required, Validators.minLength(8), Validators.pattern(/[a-zA-Z0-9]+/)
        ]
      })
    });
  }

  ngAfterViewInit(): void {
    this._handleAnimationStarts();
    this._handleReceiverRegisteredEvent();
  }

  ngOnInit() {
    this._loadLaunchers();

    this._subscriptions.add(this._contentSliderStateService.activeSlideIndex$.subscribe(async (activeSlideIndex: number) => {
      if (activeSlideIndex === 2 && this.cloudId !== undefined && this.groupId !== undefined) {
        await this._connectionCodeService.requestConnectionCode(this.cloudId, this.groupId);
      }
    }));
  }

  public nextSlide(): void {
    this._contentSliderService.nextSlide();
  }

  public animationCreated(animationItem: AnimationItem, animationName: string): void {
    this._lottieAnimations.set(animationName, animationItem);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _handleReceiverRegisteredEvent(): void {
    this._subscriptions.add(this._receiverAuthenticationWsClient.getReceiverRegistered$().subscribe(async (receiverRegisteredMessage: IReceiverRegisteredMessage) => {
      const lastGeneratedConnectionCode: string | null = this._connectionCodeStateService.getConnectionCode();

      const formattedLastGeneratedConnectionCode: string | undefined = lastGeneratedConnectionCode?.replaceAll("-", "").toLowerCase();

      if (receiverRegisteredMessage.connectionCode !== formattedLastGeneratedConnectionCode || !this.cloudId || !this.groupId) {
        return;
      }

      const cloudId: string = this.cloudId;
      const groupId: string = this.groupId;

      try {
        const [cloud, group, receiver]: [ICloud, IGroup, IReceiver] = await Promise.all([
          this._cloudRestClient.getCloud({cloudId}),
          this._groupRestClient.getGroup({cloudId, groupId}),
          this._receiverRestClient.getReceiver({
            cloudId,
            groupId,
            receiverId: receiverRegisteredMessage.receiverId
          })
        ]);

        if (this.selectedLauncherId) {
          this.installingLauncher$.set(true);
          try {
            await this._launcherRestClient.purchaseApplication({
              applicationId: this.selectedLauncherId,
              cloudId
            });

            await this._launcherRestClient.installApplication({
              applicationId: this.selectedLauncherId,
              receiverId: receiver.id
            });

            await this._receiverRestClient.updateReceiver({
              cloudId,
              groupId,
              receiverId: receiver.id,
              launcherId: this.selectedLauncherId
            });
          } catch {
            this._toastService.setMessage({
              title: "Launcher-Installation fehlgeschlagen",
              detail: "Receiver wurde verbunden, aber der Launcher konnte nicht installiert werden. Bitte erneut versuchen.",
              severity: ESeverity.error
            });
            return;
          } finally {
            this.installingLauncher$.set(false);
          }
        }

        this._toastService.setMessage({
          title: "Erfolgreich",
          detail: `Receiver wurde erfolgreich in Cloud registriert`,
          severity: ESeverity.success
        });

        this._sessionStateService.setActiveCloud(cloud);
        this._sessionStateService.setActiveGroup(group);
        this._sessionStateService.setActiveReceiver(this.selectedLauncherId
          ? {...receiver, launcherId: this.selectedLauncherId}
          : receiver);
        this._dialogRef.close();
        await this._router.navigate(["/de/"]);
      } catch {
        this._toastService.setMessage({
          title: "Fehlgeschlagen",
          detail: "Receiver konnte nicht abgerufen werden",
          severity: ESeverity.error
        });
      }
    }));
  }

  private _loadLaunchers(): void {
    this._launcherRestClient.getApplicationPage({
      pageNumber: 0,
      pageSize: 5
    }).then((launcherPage: IPage<ILauncher>) => {
      this.launchers$.set(launcherPage.records);
    }).catch(() => {
      this.launchers$.set([]);
    }).finally(() => {
      this.launchersLoaded$.set(true);
    });
  }

  private _handleAnimationStarts(): void {
    this._subscriptions.add(this._contentSliderStateService.activeSlideIndex$.subscribe((activeSlideIndex: number) => {
      switch (activeSlideIndex) {
        case this._contentSlides.length - 1:
          this._lottieAnimations.get("lottieCheck")?.play();
          break;
      }
    }));
  }
}
